/** @format */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbDate, NgbModal, NgbModalOptions, NgbModalRef, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/profile';
import { DelegationsService, UserService } from 'src/app/services';
import { getPayPeriods, getProfile, getUserRelatedScheduledAssignments } from 'src/app/store';
import {
	AddScheduledActingAssignment,
	AssignedDelegations,
	DelegationEntry,
	DeleteScheduledActingAssignment,
	FormSortableColumns,
	FormSortDirection,
	GetUserRelatedActingAssignments,
	PayPeriod,
	ScheduledActingAssignment,
	UpdateScheduledActingAssignment,
	UsersList,
} from 'src/app/store/application';
import * as HelperFunctions from '../../../assets/helper-functions/helper-functions';

@Component({
    selector: 'app-scheduled-acting',
    templateUrl: './scheduled-acting.component.html',
    styleUrls: ['./scheduled-acting.component.scss'],
    standalone: false
})
export class ScheduledActingComponent implements OnInit, OnDestroy {
	private ngUnsubscribe: Subject<void> = new Subject();

	scheduledActings$: Observable<ScheduledActingAssignment[]>;
	scheduledActings: ScheduledActingAssignment[] = [];
	delegations: DelegationEntry[] = [];
	actingDelegationsMap: AssignedDelegations[] = [];

	profile$: Observable<Profile>;
	profile!: Profile;

	payPeriods$: Observable<PayPeriod[]>;
	payPeriods!: PayPeriod[];

	allUsers: UsersList[] = [];
	usersList: UsersList[] = [];

	@ViewChild('deleteModal') deleteModal: any;

	actingForm: UntypedFormGroup;
	deleteModalRef!: NgbModalRef;

	userIdToDelete: number = -1;
	actingToDelete: number = -1;
	isEdit: boolean = false;

	readonly ngbModalOptions: NgbModalOptions = {
		backdrop: 'static',
		keyboard: false,
		centered: true,
	};

	readonly DescTextLimit: number = 100;
	readonly JobTypeTextLimit: number = 6;
	readonly JobStepTextLimit: number = 5;

	minDateFrom = new NgbDate(2022, 1, 1);
	minDateTo = new NgbDate(2022, 1, 1);

	activeOptions = [
		{ text: 'Active', value: true },
		{ text: 'Inactive', value: false },
	];

	allFormsValid = false;
	formStateValidity: any = {
		user: false,
		from: false,
		to: false,
		description: false,
	};

	formTouchedState: any = {
		user: false,
		from: false,
		to: false,
		description: false,
	};

	formSortState: FormSortableColumns = {
		fromUserSort: false,
		toUserSort: false,
		startDateSort: true,
		endDateSort: false,
		statusSort: false,
	};

	formSortDirection: FormSortDirection = {
		ascending: false,
		descending: true,
	};

	constructor(
		private store: Store<{}>,
		private modalService: NgbModal,
		private userService: UserService,
		private delegationsService: DelegationsService,
		private offcanvasService: NgbOffcanvas
	) {
		// this.scheduledActings$ = this.store.select(getAllScheduledAssignments);
		this.scheduledActings$ = this.store.select(getUserRelatedScheduledAssignments);
		this.profile$ = this.store.select(getProfile);
		this.payPeriods$ = this.store.select(getPayPeriods);

		this.actingForm = new UntypedFormGroup({
			id: new UntypedFormControl(-1),
			user: new UntypedFormControl(null, Validators.required),
			from: new UntypedFormControl(null, Validators.required),
			to: new UntypedFormControl(null, Validators.required),
			description: new UntypedFormControl('', Validators.required),
			rate: new UntypedFormControl(null),
			jobType: new UntypedFormControl(null),
			jobStep: new UntypedFormControl(null),
			active: new UntypedFormControl(true),
		});

		document.addEventListener('click', this.handleListeners.bind(this));
		document.addEventListener('keydown', this.handleListeners.bind(this));

		this.profile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((profile) => (this.profile = profile));
		this.payPeriods$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payPeriods) => (this.payPeriods = payPeriods));

		this.scheduledActings$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((scheduledActings) => {
			this.scheduledActings = [...scheduledActings];
			this.setFormSortedColumn('', true);
		});

		this.userService
			.getUsers()
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((users) => {
				users.forEach((user) => {
					this.allUsers.push({
						id: user.id!,
						fullName: `${user.firstName} ${user.lastName}`,
					});
				});
			});

		this.userService
			.getManagedUsers()
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((users) => {
				users.forEach((user) => {
					this.usersList.push({
						id: user.id!,
						fullName: `${user.firstName} ${user.lastName}`,
					});
				});

				this.usersList.sort((a: UsersList, b: UsersList) => {
					if (a.fullName < b.fullName) return -1;
					if (a.fullName > b.fullName) return 1;
					return 0;
				});
			});

		this.delegationsService
			.getAllDelegations()
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((delegations) => {
				this.delegations = delegations;
			});

		this.store.dispatch(GetUserRelatedActingAssignments());
	}

	ngOnInit(): void {}

	ngOnDestroy(): void {
		document.removeAllListeners!('click');
		document.removeAllListeners!('keydown');
	}

	getIsColumnSorted(columnSortType: string) {
		let columnKey = columnSortType as keyof FormSortableColumns;
		return this.formSortState[columnKey];
	}

	getSortIcon(columnSortType: string) {
		let columnKey = columnSortType as keyof FormSortableColumns;
		// if (!this.formSortState[columnKey]) return `<i class="fa-solid fa-bars-sort"></i>`;
		if (!this.formSortState[columnKey]) return `<i class="fa-solid fa-sort"></i>`;
		if (this.formSortDirection.ascending) return `<i class="fa-solid fa-sort-up"></i>`;
		return '<i class="fa-solid fa-sort-down"></i>';
	}

	getIsAscending = () => this.formSortDirection.ascending;

	setFormSortedColumn(columnSortType: string = '', isDataReloadSort: boolean = false) {
		if (!columnSortType.length) {
			if (this.formSortState.fromUserSort) columnSortType = 'fromUserSort';
			else if (this.formSortState.toUserSort) columnSortType = 'toUserSort';
			else if (this.formSortState.startDateSort) columnSortType = 'startDateSort';
			else if (this.formSortState.endDateSort) columnSortType = 'endDateSort';
			else columnSortType = 'statusSort';
		}

		let columnKey = columnSortType as keyof FormSortableColumns;

		// if we are reversing the sort by clicking on the currently sorted column, then flip the sort
		if (this.formSortState[columnKey]) {
			// we dont want swap the sort order if it is caused by a new entry. Only when it is clicked do we resort
			if (!isDataReloadSort) {
				this.formSortDirection.ascending = !this.formSortDirection.ascending;
				this.formSortDirection.descending = !this.formSortDirection.descending;
			}
		} else {
			this.formSortState = {
				fromUserSort: false,
				toUserSort: false,
				startDateSort: false,
				endDateSort: false,
				statusSort: false,
			};

			this.formSortState[columnKey] = true;
			this.formSortDirection.descending = true;
			this.formSortDirection.ascending = false;
		}

		this.resortFormData();
	}

	resortFormData() {
		let sortedActings: ScheduledActingAssignment[] = [];

		if (this.formSortState.fromUserSort) {
			sortedActings = [...this.scheduledActings]
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => b.from.getTime() - a.from.getTime())
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => {
					let aFullName = this.getUserFromUserId(a.forUserId!);
					let bFullName = this.getUserFromUserId(b.forUserId!);

					if (aFullName > bFullName) return this.formSortDirection.ascending ? 1 : -1;
					if (aFullName < bFullName) return this.formSortDirection.ascending ? -1 : 1;
					return 0;
				});
		}

		if (this.formSortState.toUserSort) {
			sortedActings = [...this.scheduledActings]
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => b.from.getTime() - a.from.getTime())
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => {
					let aFullName = a.description;
					let bFullName = b.description;

					if (aFullName > bFullName) return this.formSortDirection.ascending ? 1 : -1;
					if (aFullName < bFullName) return this.formSortDirection.ascending ? -1 : 1;
					return 0;
				});
		}

		if (this.formSortState.startDateSort) {
			sortedActings = [...this.scheduledActings].sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => {
				return this.formSortDirection.ascending ? a.from.getTime() - b.from.getTime() : b.from.getTime() - a.from.getTime();
			});
		}

		if (this.formSortState.endDateSort) {
			sortedActings = [...this.scheduledActings].sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => {
				return this.formSortDirection.ascending ? a.to.getTime() - b.to.getTime() : b.to.getTime() - a.to.getTime();
			});
		}

		if (this.formSortState.statusSort) {
			sortedActings = [...this.scheduledActings]
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => b.from.getTime() - a.from.getTime())
				.sort((a: ScheduledActingAssignment, b: ScheduledActingAssignment) => {
					return a.active === b.active
						? 0
						: a.active
						? this.formSortDirection.ascending
							? -1
							: 1
						: this.formSortDirection.ascending
						? 1
						: -1;
				});
		}

		this.scheduledActings = sortedActings;
	}

	// getActingForList(actingId: number) {
	// 	return this.actingDelegationsMap.find((map) => map.actingId === actingId)?.actingForNames || '';
	// }

	// findMatchingActingForUser(userId: number, actingId: number) {
	// 	let actingRecord = this.scheduledActings.find((acting) => acting.id === actingId);
	// 	let actingFrom = actingRecord!.from;
	// 	let actingTo = actingRecord!.to;
	// 	let mapIndex = this.actingDelegationsMap.findIndex((assignment) => assignment.actingId === actingId);

	// 	let matchingDelegations = this.getUserMatchedDelegations(userId);
	// 	if (matchingDelegations.length) {
	// 		let boundedDelegations = this.getRelevantDelegations(matchingDelegations, actingFrom, actingTo);

	// 		for (let delegation of boundedDelegations) {
	// 			if (!this.actingDelegationsMap[mapIndex].delegationIds.includes(delegation.id!)) {
	// 				this.actingDelegationsMap[mapIndex].delegationIds.push(delegation.id!);
	// 			}
	// 		}

	// 		let actingForNameList = this.extractActingForUser(actingId);
	// 		this.actingDelegationsMap[mapIndex].actingForNames = actingForNameList;
	// 	} else {
	// 		this.actingDelegationsMap[mapIndex].actingForNames = 'Other';
	// 	}
	// }

	// getUserMatchedDelegations = (userId: number) => this.delegations.filter((delegation) => delegation.toUserId === userId);

	// getRelevantDelegations(filteredDelegations: DelegationEntry[], startDate: Date, endDate: Date) {
	// 	let boundedDelegations: DelegationEntry[] = [];
	// 	filteredDelegations.forEach((delegation) => {
	// 		let isDelegationStartBound = HelperFunctions.getDateIsWithinBounds(startDate, endDate, delegation.from);
	// 		let isDelegationEndBound = HelperFunctions.getDateIsWithinBounds(startDate, endDate, delegation.to);
	// 		let isDelegationBound = isDelegationStartBound || isDelegationEndBound;

	// 		let isActingStartBound = HelperFunctions.getDateIsWithinBounds(delegation.from, delegation.to, startDate);
	// 		let isActingEndBound = HelperFunctions.getDateIsWithinBounds(delegation.from, delegation.to, endDate);
	// 		let isActingBound = isActingStartBound || isActingEndBound;

	// 		if (isDelegationBound || isActingBound) {
	// 			boundedDelegations.push(delegation);
	// 		}
	// 	});

	// 	return boundedDelegations;
	// }

	// extractActingForUser(actingId: number) {
	// 	let actingAssignments = this.actingDelegationsMap.find((assignment) => assignment.actingId === actingId);
	// 	if (!actingAssignments) return 'Other';

	// 	let usersDelegatedFor = '';
	// 	actingAssignments.delegationIds.forEach((delegationId) => {
	// 		let delegationEntry = this.delegations.find((delegation) => delegation.id === delegationId);
	// 		let delegationFromUserId = delegationEntry!.fromUserId;
	// 		let delegatingUser = this.allUsers.find((user) => user.id === delegationFromUserId);

	// 		if (usersDelegatedFor.length === 0) usersDelegatedFor += delegatingUser!.fullName;
	// 		else usersDelegatedFor += `, ${delegatingUser!.fullName}`;
	// 	});

	// 	return usersDelegatedFor.length > 0 ? usersDelegatedFor : 'Other';
	// }

	handleListeners(event: any) {
		if (
			event.target?.className === 'd-block modal fade show modal-static' ||
			event.target?.className === 'offcanvas-backdrop fade' ||
			event.key === 'Escape'
		) {
			if (this.modalService.hasOpenModals()) {
				this.closeDeleteModal(false);
			}

			if (this.offcanvasService.hasOpenOffcanvas() || !this.modalService.hasOpenModals()) {
				this.closeSidePanel();
			}
		}
	}

	shouldDarkenEntryBG(date: NgbDate) {
		const payPeriod = this.payPeriods.findIndex((d) => this.isInPP(date, new Date(d.startDate), new Date(d.endDate)));
		if (payPeriod > -1) {
			return payPeriod % 2 === 0;
		}
		return false;
	}

	isInPP(targetDate: NgbDate, start: Date, end: Date) {
		const ngbStart = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
		const ngbEnd = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
		if (targetDate.equals(ngbStart) || targetDate.equals(ngbEnd) || (targetDate.after(ngbStart) && targetDate.before(ngbEnd))) {
			return true;
		}
		return false;
	}

	getUserFromUserId(userId: number) {
		return this.usersList.find((user) => user.id === userId)?.fullName || '';
	}

	getDateString(date: Date) {
		return HelperFunctions.getDateString(date);
	}

	convertAndFormatRate(rate: number | null) {
		if (!rate) {
			return '';
		}

		return this.convertToCurrency(rate);
	}

	convertToCurrency(number: number) {
		const formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'CAD',
		});

		return formatter.format(number);
	}

	onDateSelect(date: NgbDate, formControl: string) {
		let selectedDate = new Date(date.year, date.month - 1, date.day);
		this.actingForm.controls[formControl].setValue(this.getDateString(selectedDate));
		this.doErrorChecking(formControl);

		if (formControl === 'from') {
			this.minDateTo = new NgbDate(date.year, date.month, date.day);
			let toDateStr = this.actingForm.controls['to'].value;

			if (toDateStr !== null) {
				let toDate = new Date(toDateStr);
				let differenceInDays = HelperFunctions.getDifferenceInDays(selectedDate, toDate);

				if (differenceInDays < 0) {
					this.actingForm.controls['to'].reset();

					this.formStateValidity.to = false;
					this.allFormsValid = false;
				}
			}
		}
	}

	isSelectedDate(date: NgbDate, formControl: string) {
		let selectedDateValue = this.actingForm.controls[formControl].value;

		if (selectedDateValue !== null) {
			let selectedDate = new Date(selectedDateValue);
			return selectedDate.getDate() === date.day && selectedDate.getMonth() + 1 === date.month && selectedDate.getFullYear() === date.year;
		}

		return false;
	}

	doErrorChecking(formControl: string, userTouchedOverride: boolean = false) {
		this.formTouchedState[formControl] = this.actingForm.controls[formControl].touched || userTouchedOverride;
		this.formStateValidity[formControl] = this.actingForm.controls[formControl].valid;
		this.allFormsValid = Array.from(Object.values(this.formStateValidity)).every((state) => state === true);
	}

	closeDeleteModal(shouldDelete: boolean) {
		this.deleteModalRef.close(shouldDelete);
	}

	deleteActing(acting: ScheduledActingAssignment) {
		this.actingToDelete = acting.id!;
		this.userIdToDelete = acting.forUserId!;

		this.deleteModalRef = this.modalService.open(this.deleteModal, this.ngbModalOptions);
		this.deleteModalRef.result.then((shouldDelete: boolean) => {
			if (shouldDelete) {
				this.store.dispatch(DeleteScheduledActingAssignment({ id: this.actingToDelete }));
			}

			this.actingToDelete = -1;
			this.userIdToDelete = -1;
		});
	}

	openSidePanel(sidePanel: any) {
		this.closeAnyOffCanvas();
		this.offcanvasService.open(sidePanel, { ariaLabelledBy: 'offcanvas-basic-title', position: 'end' });
	}

	openEditSidePanel(acting: ScheduledActingAssignment, sidePanel: any) {
		this.actingForm.controls.id.setValue(acting.id);
		this.actingForm.controls.user.setValue(acting.forUserId);
		this.actingForm.controls.from.setValue(this.getDateString(acting.from));
		this.actingForm.controls.to.setValue(this.getDateString(acting.to));
		this.actingForm.controls.description.setValue(acting.description);
		this.actingForm.controls.rate.setValue(acting.rate);
		this.actingForm.controls.jobType.setValue(acting.jobType);
		this.actingForm.controls.jobStep.setValue(acting.jobStep);
		this.actingForm.controls.active.setValue(acting.active);

		this.formStateValidity = {
			user: true,
			from: true,
			to: true,
			description: true,
		};

		this.allFormsValid = true;
		this.isEdit = true;

		this.closeAnyOffCanvas();
		this.openSidePanel(sidePanel);
	}

	closeSidePanel() {
		this.closeAnyOffCanvas();
		this.handleFormCleanup();
	}

	closeAnyOffCanvas() {
		if (this.offcanvasService.hasOpenOffcanvas()) {
			this.offcanvasService.dismiss();
		}
	}

	submitActingForm() {
		let result = this.actingForm.value;
		let scheduledActingAssignment: ScheduledActingAssignment = {
			id: result.id ? result.id : -1,
			forUserId: result.user,
			from: HelperFunctions.getBeginningOfDayDate(new Date(result.from)),
			to: HelperFunctions.getBeginningOfDayDate(new Date(result.to)),
			description: result.description,
			rate: result.rate,
			active: result.active || false,
			jobType: result.jobType,
			jobStep: result.jobStep,
		};

		if (this.isEdit) {
			this.store.dispatch(UpdateScheduledActingAssignment({ scheduledActingAssignment }));
		} else {
			this.store.dispatch(AddScheduledActingAssignment({ scheduledActingAssignment }));
		}

		this.closeSidePanel();
	}

	handleFormCleanup() {
		setTimeout(() => this.actingForm.reset(), 500);
		this.isEdit = false;
		this.allFormsValid = false;
		this.minDateTo = new NgbDate(2022, 1, 1);

		this.formStateValidity = {
			user: false,
			from: false,
			to: false,
			description: false,
		};

		this.formTouchedState = {
			user: false,
			from: false,
			to: false,
			description: false,
		};
	}
}
